/* eslint-env jquery */
/**
 * Product Variant Grid Component.
 */
import { Component, registerComponent } from 'js/component.js';
import Cookies from 'cookies-js';

const componentWrapper = 'product-variant-grid';
const componentName = 'cmp-product-variant-grid';

class ProductVariantGrid extends Component {
    /**
     * Component Setup
     */
    elements = {
        body: document.body,
        betaExperience: this.element.querySelector(`.${componentName}__beta-experience`),
        defaultExperience: this.element.querySelector(`.${componentName}__default-experience`),
        loader: this.element.querySelector('.loader'),
        prefAvailFlag: this.element.querySelector(`.${componentName}__preferred-availability-flag`),
        infoButton: this.element.querySelector(`.${componentName}__temp-buttons > .${componentName}__info-button`),
        docButton: this.element.querySelector(`.${componentName}__temp-buttons > .${componentName}__documentation-button`),
        table: this.element.querySelector(`.${componentName}__table`),
        tableBody: this.element.querySelector(`.${componentName}__table > tbody`),
        tableHeader: this.element.querySelector(`.${componentName}__table-header`),
        cardsContainer: this.element.querySelector(`.${componentName}__cards-container`),
        paginationInfoContainer: this.element.querySelector(`.${componentName}__pagination-info-container`),
        paginationSelect: this.element.querySelector(`.${componentName}__pagination-select`),
        paginationPageNumInput: this.element.querySelector(`.${componentName}__page-num-input`),
        paginationPreviousButton: this.element.querySelector(`.${componentName}__previous-button`),
        paginationNextButton: this.element.querySelector(`.${componentName}__next-button`),
        searchInputBox: this.element.querySelector(`.${componentName}__search-box`),
        defaultSelector: this.element.querySelector(`.${componentName}__default-selector`),
        defaultSelectorButton: this.element.querySelector(`.${componentName}__default-selector-button`),
        betaSelectorButton: this.element.querySelector(`.${componentName}__beta-selector-button`),
        noResultsMessage: this.element.querySelector(`.${componentName}__no-results-message-container`),
        noResultsQuery: this.element.querySelector(`.${componentName}__no-results-query`),
        searchConfiguration: document.body.querySelector('.search__configuration')
    };

    digitalData = window.digitalData = window.digitalData || [];

    isBetaExperienceAvailable = this.elements.defaultExperience.getAttribute('data-beta-experience-available');

    results = {
        searchText: '',
        products: []
    };

    paginationInformation = {
        itemsPerPage: 10,
        startRange: 0,
        endRange: 0,
        totalNumberOfResults: 0,
        currentPage: 1,
        totalNumberOfPages: 0
    };

    paginationInformationForMobile = {
        itemsPerPage: 5,
        startRange: 0,
        endRange: 0,
        totalNumberOfResults: 0,
        currentPage: 1,
        totalNumberOfPages: 0
    };

    // Define constants
    BETA_SELECTED_CLASS = `${componentWrapper}--beta-selected`;
    PREFERRED_EXPERIENCE_COOKIE_NAME = 'variantGridPreferredExperience';
    BETA = 'beta';
    DEFAULT = 'default';
    DEFAULT_LANGUAGE = 'en';
    RA_SELECT_DATA_LAYER_TYPE = 'Standard';
    BETA_DATA_LAYER_TYPE = 'New';

    // Define the query
    mediaQuery = window.matchMedia('(max-width: 768px)');

    /**
     * Modify product results state.
     */
    setResults = (searchText, results) => {
        this.results.searchText = searchText;
        this.results.products = results;
    };

    /**
     * Modify pagination state for Desktop.
     */
    setPaginationState = (itemsPerPage, startRange, endRange, totalNumberOfResults, currentPage, totalNumberOfPages) => {
        this.paginationInformation = {
            itemsPerPage, startRange, endRange, totalNumberOfResults, currentPage, totalNumberOfPages
        };
    };

    /**
     * Modify pagination state for Mobile.
     */
    setPaginationStateForMobile = (itemsPerPage, startRange, endRange, totalNumberOfResults, currentPage, totalNumberOfPages) => {
        this.paginationInformationForMobile = {
            itemsPerPage, startRange, endRange, totalNumberOfResults, currentPage, totalNumberOfPages
        };
    };

    /**
     * Initialize Component
     */
    init() {
        const checkPreferredExperience = () => {
            const preferredExperience = Cookies.get(this.PREFERRED_EXPERIENCE_COOKIE_NAME);
            const defaultExperience = this.elements.defaultExperience.getAttribute('data-defaultExperience') === 'pvg' ? this.BETA : this.DEFAULT;
            const overrideGlobalDefaultExperience = this.elements.defaultExperience.getAttribute('data-overrideGlobalDefaultExperience') === 'true';
            let endExperience;

            if (overrideGlobalDefaultExperience) {
                endExperience = defaultExperience;
            } else if ((preferredExperience && preferredExperience === this.BETA) || (defaultExperience === this.BETA)) {
                endExperience = this.BETA;
            } else {
                endExperience = this.DEFAULT;
            }

            if (endExperience === this.BETA) {
                this.element.classList.add(this.BETA_SELECTED_CLASS);
                this.digitalData.push({
                    event: 'productSelectionTool',
                    productSelector: {
                        productSelectionToolType: this.BETA_DATA_LAYER_TYPE
                    }
                });
            } else {
                this.digitalData.push({
                    event: 'productSelectionTool',
                    productSelector: {
                        productSelectionToolType: this.RA_SELECT_DATA_LAYER_TYPE
                    }
                });
            }
        };

        if (this.isBetaExperienceAvailable === 'true') {
            this.resourcePath = this.elements.betaExperience.getAttribute('data-resource-path');
            this.homePagePath = this.elements.betaExperience.getAttribute('data-home-page-path');
            this.pageLanguage = this.elements.betaExperience.getAttribute('data-language');
            this.pageCountry = this.elements.betaExperience.getAttribute('data-country');

            this.headerTextCatalogNumber = this.elements.tableHeader.querySelector(`.${componentName}__column-header-catalogNumber`).innerText;
            this.headerTextDescription = this.elements.tableHeader.querySelector(`.${componentName}__column-header-description`).innerText;
            this.headerTextPrefAvail = this.elements.tableHeader.querySelector(`.${componentName}__column-header-prefAvail`).innerText;
            this.headerTextLifecycle = this.elements.tableHeader.querySelector(`.${componentName}__column-header-lifecycle`).innerText;
            this.headerTextQuickActions = this.elements.tableHeader.querySelector(`.${componentName}__column-header-actions`).innerText;

            this.configuratorUrl = this.elements.betaExperience.getAttribute('data-configurator-url');
            this.configuratorHost = this.configuratorUrl ? this.configuratorUrl.substring(0, this.configuratorUrl.indexOf('.com') + 4) : 'https://configurator.rockwellautomation.com';

            this.searchBaseUrl = this.elements.searchConfiguration ? this.elements.searchConfiguration.getAttribute('data-search-base-url') : '';
            this.searchBaseUrl = this.searchBaseUrl ? this.searchBaseUrl : 'https://rockwellautomation.com/search';

            this.addEventListeners();
            this.loadDynamicContent(1, 10, 0);
            checkPreferredExperience();
        }

        const { digitalData } = this;

        // Desktop Data Layer
        $(`.${componentName}__table > tbody`).on('mousedown', 'a', function() { // eslint-disable-line func-names
            const row = $(this).closest(`.${componentName}__table-row`);
            const paginationSelectValue = $(`.${componentName}__pagination-select`).val();
            const currentPageNumber = $(`.${componentName}__page-num-input`).val();
            const linkText = $(this).attr('title');
            const linkRanking = row.index() + 1 + ((currentPageNumber - 1) * paginationSelectValue);
            const productSelectedResultClick = row.find(`.${componentName}__catalog-num-link`).text();
            const searchResultDestinationURL = $(this).prop('href');
            const searchResultSelectionPosition = row.index() + 1;

            digitalData.push({
                event: 'clickSearchResult',
                searchResult: {
                    linkText,
                    linkRanking,
                    productSelectedResultClick,
                    searchResultDestinationURL,
                    searchResultSelectionPosition
                }
            });
        });

        // Mobile Data Layer
        $(`.${componentName}__cards-container`).on('mousedown', 'a', function() { // eslint-disable-line func-names
            const row = $(this).closest(`.${componentName}__card`);
            const paginationSelectValue = 5;
            const currentPageNumber = $(`.${componentName}__page-num-input`).val();
            const linkText = $(this).attr('title');
            const linkRanking = row.index() + 1 + ((currentPageNumber - 1) * paginationSelectValue);
            const productSelectedResultClick = row.find(`.${componentName}__catalog-num-link`).text();
            const searchResultDestinationURL = $(this).prop('href');
            const searchResultSelectionPosition = row.index() + 1;

            digitalData.push({
                event: 'clickSearchResult',
                searchResult: {
                    linkText,
                    linkRanking,
                    productSelectedResultClick,
                    searchResultDestinationURL,
                    searchResultSelectionPosition
                }
            });
        });

    }

    generateDesktopTableRow = (product) => `
        <tr class="${componentName}__table-row">
            <td data-label="Catalog Number">
                <a class="${componentName}__catalog-num-link" href="${product.productDetailsPageLink}" title="${product.catalogNumber.trim()}">${product.catalogNumber.trim()}</a>
            </td>
            <td data-label="Description">
                ${product.description.trim()}
            </td>
            <td data-label="Best Selling">
                ${product.lifeCycleStatus.trim() === 'Discontinued' ? '' : product.preferredAvailability}
            </td>
            <td class="${componentName}__lifecycle-status" data-label="Lifecycle" data-lifecycle="${product.lifeCycleStatus.trim()}">
                ${product.lifeCycleStatus.trim()}
            </td>
            <td data-label="Quick Actions">
                <a class="${componentName}__info-button-link" target="_blank" href="${product.productConfigPageLink}" title="Product Info">${this.elements.infoButton.outerHTML}</a>
                <a class="${componentName}__documentation-button-link" target="_blank" href="${product.productCatalogSearchPageLink}" title="Product Documentation">${this.elements.docButton.outerHTML}</a>
            </td>
        </tr>
    `;

    generateDesktopTableRows = (products) => {
        const desktopTableRows = products.map(this.generateDesktopTableRow);
        return desktopTableRows.join('');
    };

    generateMobileProductCard = (product) => `
        <div class="${componentName}__card">
            <dl class="${componentName}__def-list">
                <div class="${componentName}__card-row">
                    <dt>${this.headerTextCatalogNumber}</dt>
                    <dd data-label="Catalog Number">
                        <a class="${componentName}__catalog-num-link" href="${product.productDetailsPageLink}" title="${product.catalogNumber.trim()}">${product.catalogNumber.trim()}</a>
                    </dd>
                </div>
                <div class="${componentName}__card-row">
                    <dt>${this.headerTextDescription.trim()}</dt>
                    <dd data-label="Description">
                        ${product.description.trim()}
                    </dd>
                </div>
                ${product.preferredAvailability === '' || product.lifeCycleStatus.trim() === 'Discontinued' ? '' : `<div class="${componentName}__card-row">
                    <dt>${this.headerTextPrefAvail}</dt>
                    <dd class="${componentName}__preferred-availability" data-label="Best Selling">
                        ${product.preferredAvailability}
                    </dd>
                </div>`}
                <div class="${componentName}__card-row">
                    <dt>${this.headerTextLifecycle}</dt>
                    <dd class="${componentName}__lifecycle-status" data-label="Lifecycle" data-lifecycle="${product.lifeCycleStatus.trim()}">
                        ${product.lifeCycleStatus.trim()}
                    </dd>
                </div>
                <div class="${componentName}__card-row">
                    <dt>${this.headerTextQuickActions}</dt>
                    <dd data-label="Quick Actions">
                        <a class="${componentName}__info-button-link" target="_blank" href="${product.productConfigPageLink}" title="Product Info">${this.elements.infoButton.outerHTML}</a>
                        <a class="${componentName}__documentation-button-link" target="_blank" href="${product.productCatalogSearchPageLink}" title="Product Documentation">${this.elements.docButton.outerHTML}</a>
                    </dd>
                </div>
            </dl>
        </div>
    `;

    generateMobileProductCards = (products) => {
        const mobileProductCards = [];
        for (let i = 0; i < 5; i += 1) {
            if (products[i]) {
                mobileProductCards.push(this.generateMobileProductCard(products[i]));
            }
        }
        return mobileProductCards.join('');
    };

    generateNoResultsMarkupDesktop = (noResultsMessageHtmlStr) => `
        <tr class="${componentName}__no-results-row ${componentName}__table-row">
            <td>${noResultsMessageHtmlStr}</td>
            <td></td>
            <td></td>
            <td></td>
        </tr>
    `;

    generateNoResultsMarkupMobile = (noResultsMessageHtmlStr) => `
        <div class="${componentName}__card ${componentName}__no-results-card">
            ${noResultsMessageHtmlStr}
        </div>
    `;

    modifyTableAndCards = (desktopTableRows, mobileProductCards) => {
        this.elements.tableBody.innerHTML = desktopTableRows;
        this.elements.cardsContainer.innerHTML = mobileProductCards;
    };

    modifyPaginationBar = (paginationInfo) => {
        const {
            startRange, endRange, totalNumberOfResults, currentPage, totalNumberOfPages
        } = paginationInfo;

        const modifyNumberOfResults = () => {
            const currentRange = this.elements.paginationInfoContainer.querySelector(`.${componentName}__current-range`);
            const totalResults = this.elements.paginationInfoContainer.querySelector(`.${componentName}__total-results`);
            if (startRange === 0 && endRange === 0) {
                currentRange.innerText = `${startRange}`;
            } else {
                currentRange.innerText = `${startRange}-${endRange}`;
            }
            totalResults.innerText = totalNumberOfResults;
        };

        const modifyNumberOfPages = () => {
            const pageNumInput = this.elements.paginationInfoContainer.querySelector(`.${componentName}__page-num-input`);
            const noOfPages = this.elements.paginationInfoContainer.querySelector(`.${componentName}__no-of-pages`);
            pageNumInput.value = currentPage;
            noOfPages.innerText = totalNumberOfPages;
        };

        modifyNumberOfResults(startRange, endRange, totalNumberOfResults);
        modifyNumberOfPages(currentPage, totalNumberOfPages);

        const isPreviousButtonDisabled = (currentPage === 1);
        this.elements.paginationPreviousButton.disabled = isPreviousButtonDisabled;
        if (isPreviousButtonDisabled) {
            this.elements.paginationPreviousButton.classList.add(`${componentName}__previous-button--disabled`);
        } else {
            this.elements.paginationPreviousButton.classList.remove(`${componentName}__previous-button--disabled`);
        }

        const isNextButtonDisabled = (currentPage === totalNumberOfPages);
        this.elements.paginationNextButton.disabled = isNextButtonDisabled;
        if (isNextButtonDisabled) {
            this.elements.paginationNextButton.classList.add(`${componentName}__next-button--disabled`);
        } else {
            this.elements.paginationNextButton.classList.remove(`${componentName}__next-button--disabled`);
        }
    };

    addEventListeners = () => {

        this.elements.paginationSelect.addEventListener('change', (evt) => {
            this.elements.defaultSelector.scrollIntoView();
            this.loadDynamicContent(1, parseInt(evt.target.value, 10), 0, this.results.searchText);
        });

        this.elements.paginationPreviousButton.addEventListener('click', () => {
            this.elements.defaultSelector.scrollIntoView();
            if (this.mediaQuery.matches) {
                this.loadContent((this.paginationInformationForMobile.currentPage - 1), this.results.searchText);
            } else {
                this.loadDynamicContent((this.paginationInformation.currentPage - 1), this.paginationInformation.itemsPerPage, 0, this.results.searchText);
            }
        });

        this.elements.paginationNextButton.addEventListener('click', () => {
            this.elements.defaultSelector.scrollIntoView();
            if (this.mediaQuery.matches) {
                this.loadContent((this.paginationInformationForMobile.currentPage + 1), this.results.searchText);
            } else {
                this.loadDynamicContent((this.paginationInformation.currentPage + 1), this.paginationInformation.itemsPerPage, 0, this.results.searchText);
            }
        });

        // Init a timeout variable to be used below
        let paginationPageNumInputTimeout = null;

        // Listen for keystroke events
        this.elements.paginationPageNumInput.addEventListener('input', (evt) => {
            clearTimeout(paginationPageNumInputTimeout);
            const productVariantClass = this;
            paginationPageNumInputTimeout = setTimeout(() => {
                const inputText = evt.target.value;
                const processedInput = inputText.replace(/e|\+|\./gi, '');

                let pageNumInput = parseInt(processedInput, 10);

                this.elements.defaultSelector.scrollIntoView();

                if (pageNumInput < 1 || Number.isNaN(pageNumInput) === true) {
                    pageNumInput = 1;
                }

                if (productVariantClass.mediaQuery.matches) {
                    if (pageNumInput > productVariantClass.paginationInformationForMobile.totalNumberOfPages) {
                        pageNumInput = productVariantClass.paginationInformationForMobile.totalNumberOfPages;
                    }
                    productVariantClass.loadContent(pageNumInput, productVariantClass.results.searchText);
                } else {
                    if (pageNumInput > productVariantClass.paginationInformation.totalNumberOfPages) {
                        pageNumInput = productVariantClass.paginationInformation.totalNumberOfPages;
                    }
                    productVariantClass.loadDynamicContent(pageNumInput, productVariantClass.paginationInformation.itemsPerPage, 0, productVariantClass.results.searchText);
                }
            }, 1000);
        });

        if (this.elements.searchInputBox) {
            let searchInputTimeout = null;

            // Listen for keystroke events
            this.elements.searchInputBox.addEventListener('input', (evt) => {
                clearTimeout(searchInputTimeout);
                const productVariantClass = this;
                this.elements.paginationPageNumInput.disabled = false;
                this.elements.paginationSelect.disabled = false;
                searchInputTimeout = setTimeout(() => {
                    const searchText = evt.target.value;
                    if (searchText.length >= 2) {
                        productVariantClass.loadDynamicContent(1, productVariantClass.paginationInformation.itemsPerPage, 0, searchText);
                    } else {
                        productVariantClass.loadDynamicContent(1, productVariantClass.paginationInformation.itemsPerPage, 0);
                    }
                }, 1000);
            });
        }

        if (this.mediaQuery.addEventListener) {
            this.mediaQuery.addEventListener('change', (evt) => {
                if (evt.matches) {
                    this.modifyPaginationBar(this.paginationInformationForMobile);
                } else {
                    this.modifyPaginationBar(this.paginationInformation);
                }
            });
        } else {
            this.mediaQuery.addListener((evt) => {
                if (evt.matches) {
                    this.modifyPaginationBar(this.paginationInformationForMobile);
                } else {
                    this.modifyPaginationBar(this.paginationInformation);
                }
            });
        }

        // Experience toggle functionality
        this.elements.defaultSelectorButton.addEventListener('click', () => {
            this.element.classList.remove(this.BETA_SELECTED_CLASS);
            Cookies.set(this.PREFERRED_EXPERIENCE_COOKIE_NAME, this.DEFAULT, { expires: Infinity, secure: true });
            this.digitalData.push({
                event: 'productSelectorSwitch',
                productSelector: {
                    productSelectionToolType: this.RA_SELECT_DATA_LAYER_TYPE
                }
            });
        });

        this.elements.betaSelectorButton.addEventListener('click', () => {
            this.element.classList.add(this.BETA_SELECTED_CLASS);
            Cookies.set(this.PREFERRED_EXPERIENCE_COOKIE_NAME, this.BETA, { expires: Infinity, secure: true });
            this.digitalData.push({
                event: 'productSelectorSwitch',
                productSelector: {
                    productSelectionToolType: this.BETA_DATA_LAYER_TYPE
                }
            });
        });
    };

    loadContent = (pageNumberMobile, searchText) => {
        const expectedStartRangeMobile = ((pageNumberMobile - 1) * 5) + 1;

        const pageNumberDesktop = Math.ceil((expectedStartRangeMobile) / (this.paginationInformation.itemsPerPage));
        const expectedStartRangeDesktop = ((pageNumberDesktop - 1) * (this.paginationInformation.itemsPerPage)) + 1;

        const loadMobileCards = (products) => {
            const startRangeForMobile = expectedStartRangeMobile;
            const maxEndRangeForMobile = (startRangeForMobile - 1) + 5;
            const { totalNumberOfResults, totalNumberOfPages } = this.paginationInformationForMobile;
            const endRangeForMobile = (maxEndRangeForMobile > totalNumberOfResults) ? totalNumberOfResults : maxEndRangeForMobile;
            this.setPaginationStateForMobile(5, startRangeForMobile, endRangeForMobile, totalNumberOfResults, pageNumberMobile, totalNumberOfPages);

            this.elements.cardsContainer.innerHTML = this.generateMobileProductCards(products);
            this.modifyPaginationBar(this.paginationInformationForMobile);
        };

        const start = expectedStartRangeMobile - expectedStartRangeDesktop;

        if (expectedStartRangeMobile >= this.paginationInformation.startRange
            && expectedStartRangeMobile <= this.paginationInformation.endRange) {
            this.showLoader();

            const products = this.results.products.slice(start, (start + 5));
            loadMobileCards(products);

            this.hideLoader();
        } else {
            this.loadDynamicContent(pageNumberDesktop, this.paginationInformation.itemsPerPage, start, searchText);
        }
    };

    /**
     * Load Content for the Product Variant Grid.
     */
    loadDynamicContent = async (pageNumber, pageSize, mobileStart, searchText) => {
        let finalUrl = `${this.resourcePath}.productVariants.${pageNumber}.${pageSize}.json`;
        if (searchText) {
            finalUrl += `?q=${searchText}`;
        }

        // Process it to human-readable
        const processLifeCycleStatus = (statusFromJson) => {
            const allStatuses = {
                ACTIVE: 'Active',
                ACTIVE_MATURE: 'Active Mature',
                END_OF_LIFE: 'End of Life',
                DISCONTINUED: 'Discontinued',
                NOT_SET: ''
            };
            return allStatuses[statusFromJson];
        };

        // Minimize a full Product Object to a smaller object (with the needed attributes)
        const processProduct = (productJsonObj) => {
            const {
                catalogNumber,
                commercialDescription,
                technicalDescription,
                names,
                salesOrg,
                productLifeCycleStatus
            } = productJsonObj;

            const productDetailsPageLink = `${this.homePagePath}/products/details.${catalogNumber}.html`;

            const getValueFromDescriptionArr = (arr, currentLocaleCode) => {
                let isoValue = '';
                let defaultValue = '';
                if (arr && arr.length > 0) {
                    arr.forEach((descriptionObj) => {
                        const isoCode = descriptionObj.isocode;
                        if (isoCode === currentLocaleCode) {
                            isoValue = descriptionObj.value;
                        } else if (isoCode === this.DEFAULT_LANGUAGE) {
                            defaultValue = descriptionObj.value;
                        }
                    });
                }
                return isoValue || defaultValue;
            };

            const commercialDescriptionValue = getValueFromDescriptionArr(commercialDescription, this.pageLanguage);
            const technicalDescriptionValue = getValueFromDescriptionArr(technicalDescription, this.pageLanguage);
            const nameValue = getValueFromDescriptionArr(names, this.pageLanguage);
            const description = commercialDescriptionValue || technicalDescriptionValue || nameValue;

            const getPrefAvailValue = () => {
                let isPrefAvail = false;
                if (salesOrg && salesOrg.length > 0) {
                    isPrefAvail = salesOrg.some((org) => org.preferredAvailability === true);
                }
                return isPrefAvail ? `${this.elements.prefAvailFlag.outerHTML}` : '';
            };

            const preferredAvailability = getPrefAvailValue();
            const lifeCycleStatus = processLifeCycleStatus(productLifeCycleStatus);

            const productConfigPageLink = `${this.configuratorHost}/#/validate/${catalogNumber}`;
            const langCountry = (this.pageCountry != null)
                ? `${this.pageLanguage}-${this.pageCountry}`
                : `${this.pageLanguage}`;
            const productCatalogSearchPageLink = `${this.searchBaseUrl}/ra-${langCountry};keyword=${catalogNumber};startIndex=0;activeTab=Literature;spellingCorrect=true;facets=;languages=${this.pageLanguage};locales=${this.pageLanguage}_GLOBAL,${langCountry};sort=bma;isPLS=false;deepLinking=true`;

            return {
                catalogNumber,
                productDetailsPageLink,
                description,
                preferredAvailability,
                lifeCycleStatus,
                productConfigPageLink,
                productCatalogSearchPageLink
            };
        };

        const processProducts = (productsJsonArr) => productsJsonArr.map(processProduct);

        this.showLoader();

        fetch(finalUrl).then((response) => {
            if(!response.ok) {
                throw new Error('Response failed');
            }
            return response.json();
        }).then((response) => {
            if (!response) {
                return;
            }
            if (!response.products) {
                // Handle no-results
                this.setResults(searchText, []);
                this.setPaginationState(pageSize, 0, 0, 0, 1, 1);
                this.setPaginationStateForMobile(5, 0, 0, 0, 1, 1);

                this.hideLoader();
                const noResultsMessageSearchText = searchText ? `'${searchText}'` : 'this product family';
                this.elements.noResultsQuery.innerText = `${noResultsMessageSearchText}`;
                const noResultsMessageHtmlStr = this.elements.noResultsMessage.innerHTML;
                this.modifyTableAndCards(this.generateNoResultsMarkupDesktop(noResultsMessageHtmlStr), this.generateNoResultsMarkupMobile(noResultsMessageHtmlStr));
                if (this.mediaQuery.matches) {
                    this.modifyPaginationBar(this.paginationInformationForMobile);
                } else {
                    this.modifyPaginationBar(this.paginationInformation);
                }
                this.elements.paginationPageNumInput.disabled = true;
                this.elements.paginationSelect.disabled = true;
                if (searchText) {
                    this.digitalData.push({
                        event: 'useSearch',
                        search: {
                            type: 'product selection',
                            term: searchText.toLowerCase(),
                            results: 0
                        }
                    });
                }
                return;
            }

            const products = processProducts(response.products);
            this.setResults(searchText, products);

            const totalNumberOfResults = response.totalNumber;
            const noOfPages = response.numberOfPages;
            const startRange = ((pageNumber - 1) * pageSize) + 1;
            const maxEndRange = (startRange - 1) + (pageSize * 1);
            const endRange = (maxEndRange > totalNumberOfResults) ? totalNumberOfResults : maxEndRange;

            const noOfPagesForMobile = Math.ceil(totalNumberOfResults / 5);
            const startRangeForMobile = mobileStart + startRange;
            const pageNumberForMobile = ((startRangeForMobile - 1) / 5) + 1;
            const maxEndRangeForMobile = (startRangeForMobile - 1) + 5;
            const endRangeForMobile = (maxEndRangeForMobile > totalNumberOfResults) ? totalNumberOfResults : maxEndRangeForMobile;

            this.setPaginationState(pageSize, startRange, endRange, totalNumberOfResults, pageNumber, noOfPages);
            this.setPaginationStateForMobile(5, startRangeForMobile, endRangeForMobile, totalNumberOfResults, pageNumberForMobile, noOfPagesForMobile);

            const desktopTableRows = this.generateDesktopTableRows(products);
            const mobileProducts = products.slice(mobileStart, mobileStart + 5);
            const mobileProductCards = this.generateMobileProductCards(mobileProducts);

            this.hideLoader();
            this.modifyTableAndCards(desktopTableRows, mobileProductCards);
            if (this.mediaQuery.matches) {
                this.modifyPaginationBar(this.paginationInformationForMobile);
            } else {
                this.modifyPaginationBar(this.paginationInformation);
            }

            if (searchText) {
                this.digitalData.push({
                    event: 'useSearch',
                    search: {
                        type: 'product selection',
                        term: searchText.toLowerCase(),
                        results: totalNumberOfResults
                    }
                });
            }

        }).catch((err) => {
            console.error(err); // eslint-disable-line
        })
            .finally(() => {
                this.hideLoader();
            });
    };

    showLoader = () => {
        this.elements.loader.classList.add('active');
        this.elements.loader.style.display = 'flex';
    };

    hideLoader = () => {
        this.elements.loader.classList.remove('active');
        this.elements.loader.style.display = 'none';
    };
}

registerComponent(`.${componentWrapper}`, ProductVariantGrid);
